/**
 * Helpers for rendering troop-count badges in MapView.
 *
 * The numbers used to live in a filled circle on top of the territory,
 * but on dense maps the circles crowd the geometry and on very small
 * territories they don't fit at all. We now render the number directly
 * over the territory fill, picking white-vs-black to maintain WCAG-ish
 * contrast against whatever owner / unowned / highlight colour the
 * territory currently shows.
 *
 * Functions here are pure so they can be reused by anything that needs
 * the same contrast decision (e.g. tooltips, leaderboard chips).
 */

/**
 * Parse an arbitrary CSS colour string into linear-light [r, g, b] in
 * [0, 1]. Returns null when the string isn't recognised — caller falls
 * back to a safe default.
 *
 * Supported forms:
 *   - "#rgb" / "#rgba" (3 or 4 hex digits)
 *   - "#rrggbb" / "#rrggbbaa" (6 or 8 hex digits)
 *   - "rgb(r, g, b)" / "rgba(r, g, b, a)" — decimal ints
 */
function parseColor(c: string): [number, number, number] | null {
  if (!c) return null
  const s = c.trim().toLowerCase()
  // Hex
  const hex = s.match(/^#([0-9a-f]{3,8})$/)
  if (hex && hex[1]) {
    let h: string = hex[1]
    if (h.length === 3 || h.length === 4) {
      h = h
        .split('')
        .slice(0, 3)
        .map((ch) => ch + ch)
        .join('')
    } else if (h.length === 8) {
      h = h.slice(0, 6)
    }
    if (h.length !== 6) return null
    const r = parseInt(h.slice(0, 2), 16) / 255
    const g = parseInt(h.slice(2, 4), 16) / 255
    const b = parseInt(h.slice(4, 6), 16) / 255
    return [r, g, b]
  }
  // rgb()/rgba()
  const rgb = s.match(/^rgba?\(([^)]+)\)$/)
  if (rgb) {
    const parts = (rgb[1] ?? '').split(',').map((p) => p.trim())
    if (parts.length < 3) return null
    const r = parseFloat(parts[0] ?? '') / 255
    const g = parseFloat(parts[1] ?? '') / 255
    const b = parseFloat(parts[2] ?? '') / 255
    if ([r, g, b].some((n) => isNaN(n))) return null
    return [r, g, b]
  }
  return null
}

/**
 * Relative luminance per WCAG 2.x (sRGB → linear → weighted sum). We use
 * this to pick black vs white for max contrast against an arbitrary fill.
 */
function luminance(rgb: [number, number, number]): number {
  const lin = rgb.map((c) =>
    c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4),
  )
  return 0.2126 * lin[0]! + 0.7152 * lin[1]! + 0.0722 * lin[2]!
}

/**
 * Black or white, whichever reads better against `fill`. We use a
 * luminance threshold of 0.45 rather than the textbook 0.5 because on
 * the Battle Carp tan / olive palette mid-tones land near the boundary,
 * and bumping the bias slightly toward white avoids ambiguous greys
 * landing on dark text against muddy backgrounds.
 *
 * When the fill is unparseable, fall back to white — the map background
 * is dark in most themed states, so white is the safer default.
 *
 * Pure and called ~once per territory (plus per region-bonus badge) on every
 * MapView render, with only a handful of distinct fills in play, so results
 * are memoised by fill string to skip the parse + luminance math on repeats.
 */
const _contrastCache = new Map<string, '#ffffff' | '#000000'>()
export function contrastTextColor(fill: string): '#ffffff' | '#000000' {
  const cached = _contrastCache.get(fill)
  if (cached) return cached
  const rgb = parseColor(fill)
  const result: '#ffffff' | '#000000' =
    !rgb ? '#ffffff' : luminance(rgb) > 0.45 ? '#000000' : '#ffffff'
  _contrastCache.set(fill, result)
  return result
}

/**
 * Parse an SVG `d` attribute into a flat list of on-curve endpoint
 * vertices. Skips the control points of cubic/quadratic curves — for
 * polygon-overlap and nearest-point-on-polygon queries, the endpoint
 * sample is the right granularity (curve interior doesn't matter when
 * polygons are tiled or share edges).
 *
 * Returns an empty array when the path is empty or unparseable.
 */
export function parsePolygonVertices(d: string | undefined): Array<[number, number]> {
  if (!d) return []
  const tokens = d.match(/[a-zA-Z]|-?\d*\.?\d+(?:[eE][-+]?\d+)?/g)
  if (!tokens) return []
  const pts: Array<[number, number]> = []
  let cx = 0
  let cy = 0
  let startX = 0
  let startY = 0
  let cmd: string | null = null
  let i = 0
  while (i < tokens.length) {
    const t = tokens[i]!
    if (/[a-zA-Z]/.test(t)) {
      cmd = t
      i++
      continue
    }
    if (!cmd) {
      i++
      continue
    }
    const isRel = cmd === cmd.toLowerCase()
    const c = cmd.toUpperCase()
    if (c === 'M') {
      const x = parseFloat(tokens[i++]!)
      const y = parseFloat(tokens[i++]!)
      cx = isRel ? cx + x : x
      cy = isRel ? cy + y : y
      startX = cx
      startY = cy
      pts.push([cx, cy])
      cmd = isRel ? 'l' : 'L'
    } else if (c === 'L') {
      const x = parseFloat(tokens[i++]!)
      const y = parseFloat(tokens[i++]!)
      cx = isRel ? cx + x : x
      cy = isRel ? cy + y : y
      pts.push([cx, cy])
    } else if (c === 'H') {
      const x = parseFloat(tokens[i++]!)
      cx = isRel ? cx + x : x
      pts.push([cx, cy])
    } else if (c === 'V') {
      const y = parseFloat(tokens[i++]!)
      cy = isRel ? cy + y : y
      pts.push([cx, cy])
    } else if (c === 'C') {
      i += 4
      const x = parseFloat(tokens[i++]!)
      const y = parseFloat(tokens[i++]!)
      cx = isRel ? cx + x : x
      cy = isRel ? cy + y : y
      pts.push([cx, cy])
    } else if (c === 'S' || c === 'Q') {
      i += 2
      const x = parseFloat(tokens[i++]!)
      const y = parseFloat(tokens[i++]!)
      cx = isRel ? cx + x : x
      cy = isRel ? cy + y : y
      pts.push([cx, cy])
    } else if (c === 'T') {
      const x = parseFloat(tokens[i++]!)
      const y = parseFloat(tokens[i++]!)
      cx = isRel ? cx + x : x
      cy = isRel ? cy + y : y
      pts.push([cx, cy])
    } else if (c === 'A') {
      i += 5
      const x = parseFloat(tokens[i++]!)
      const y = parseFloat(tokens[i++]!)
      cx = isRel ? cx + x : x
      cy = isRel ? cy + y : y
      pts.push([cx, cy])
    } else if (c === 'Z') {
      cx = startX
      cy = startY
    } else {
      i++
    }
  }
  return pts
}

/**
 * Compute polygon area (shoelace) from an SVG `d` attribute. Good
 * enough for "is this polygon big enough for a badge" — an
 * order-of-magnitude heuristic, not exact geometry for curves.
 */
export function polygonAreaFromD(d: string | undefined): number {
  const pts = parsePolygonVertices(d)
  if (pts.length === 0) return 0
  let a = 0
  for (let k = 0; k < pts.length; k++) {
    const [x1, y1] = pts[k]!
    const [x2, y2] = pts[(k + 1) % pts.length]!
    a += x1 * y2 - x2 * y1
  }
  return Math.abs(a) / 2
}

/**
 * Find the vertex on `vertices` closest to `target`. Returns null when
 * the vertex list is empty.
 *
 * This walks each vertex (not each edge), which is the right
 * granularity for our use case: territory polygons have enough vertices
 * after extraction/jaggification that the nearest vertex is within a
 * pixel or two of the true nearest point on the edge.
 */
export function nearestVertexTo(
  vertices: Array<[number, number]>,
  target: { x: number; y: number },
): [number, number] | null {
  if (vertices.length === 0) return null
  let bestDistSq = Infinity
  let best: [number, number] = vertices[0]!
  for (const v of vertices) {
    const dx = v[0] - target.x
    const dy = v[1] - target.y
    const d = dx * dx + dy * dy
    if (d < bestDistSq) {
      bestDistSq = d
      best = v
    }
  }
  return best
}

/**
 * Find the closest pair of vertices between two polygons — i.e. the
 * narrowest gap between them. Used to anchor sea-route connector lines
 * so they always span the strait at its shortest point and never cross
 * a third territory.
 *
 * O(|a| × |b|). With typical territory polygons (≤ 50 vertices each
 * after extraction) this is a few thousand ops per pair — fine for the
 * once-per-map-mount memoized loop in MapView.
 *
 * Returns null when either input is empty.
 */
export function closestVertexPair(
  a: Array<[number, number]>,
  b: Array<[number, number]>,
): { from: [number, number]; to: [number, number] } | null {
  if (a.length === 0 || b.length === 0) return null
  let bestDistSq = Infinity
  let bestA: [number, number] = a[0]!
  let bestB: [number, number] = b[0]!
  for (const va of a) {
    for (const vb of b) {
      const dx = va[0] - vb[0]
      const dy = va[1] - vb[1]
      const d = dx * dx + dy * dy
      if (d < bestDistSq) {
        bestDistSq = d
        bestA = va
        bestB = vb
      }
    }
  }
  return { from: bestA, to: bestB }
}

/**
 * Ray-casting point-in-polygon test. Returns true when `pt` is strictly
 * inside the polygon described by `vertices`.
 */
export function pointInPolygon(
  pt: [number, number],
  vertices: Array<[number, number]>,
): boolean {
  let inside = false
  for (let i = 0, j = vertices.length - 1; i < vertices.length; j = i++) {
    const xi = vertices[i]![0]
    const yi = vertices[i]![1]
    const xj = vertices[j]![0]
    const yj = vertices[j]![1]
    if (
      yi > pt[1] !== yj > pt[1] &&
      pt[0] < ((xj - xi) * (pt[1] - yi)) / (yj - yi) + xi
    ) {
      inside = !inside
    }
  }
  return inside
}

/**
 * True when the segment from→to passes through the interior of the
 * polygon. We sample 19 evenly-spaced interior points along the
 * segment (excluding the endpoints, which may sit on the segment's
 * source/target polygon) and check whether any land inside
 * `vertices`. Used to validate sea-route connector lines: if a
 * candidate connector passes through a third territory we skip
 * drawing it entirely so dashed lines never cross land.
 */
export function lineCrossesPolygon(
  from: [number, number],
  to: [number, number],
  vertices: Array<[number, number]>,
): boolean {
  const STEPS = 20
  for (let i = 1; i < STEPS; i++) {
    const t = i / STEPS
    const x = from[0] + (to[0] - from[0]) * t
    const y = from[1] + (to[1] - from[1]) * t
    if (pointInPolygon([x, y], vertices)) return true
  }
  return false
}

/**
 * Minimum squared distance from point (px, py) to the line segment
 * from (ax, ay) to (bx, by). Uses the standard clamped-projection
 * formula so it returns the distance to the closest point ON the
 * segment, not the infinite line. The return value is squared so the
 * hot inner loop in poleOfInaccessibility avoids a sqrt.
 */
function pointToSegmentDistSq(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  const dx = bx - ax
  const dy = by - ay
  const lenSq = dx * dx + dy * dy
  if (lenSq === 0) {
    // Degenerate segment — distance to the single point.
    const ex = px - ax
    const ey = py - ay
    return ex * ex + ey * ey
  }
  // Project (px, py) onto the segment, clamped to [0, 1].
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lenSq))
  const qx = ax + t * dx
  const qy = ay + t * dy
  const fx = px - qx
  const fy = py - qy
  return fx * fx + fy * fy
}

/**
 * Compute the "pole of inaccessibility" — the point inside a polygon
 * that is furthest from any edge. Uses a grid-search approach: sample
 * an N×N grid over the bounding box, keep only cells whose centre is
 * inside the polygon, and return the one with the greatest minimum
 * distance to the nearest edge (measured point-to-segment, not
 * point-to-vertex). Falls back to the unmodified centroid when no
 * interior sample is found (degenerate polygons).
 *
 * The distance metric is point-to-segment (not point-to-vertex as
 * before). On territories with long edges and sparse vertices the
 * vertex proxy significantly underestimates true clearance; the
 * segment metric gives the correct answer for any polygon density.
 *
 * Grid resolution N=32 gives bbox/32 cell width, tight enough that
 * sub-pixel placement error is negligible for badge positioning. The
 * function is designed to be called once per territory per map mount
 * and memoised by the caller.
 *
 * @param vertices  On-curve polygon vertices (output of parsePolygonVertices).
 * @param fallback  The point to return when no interior sample is found.
 * @param N         Grid resolution per axis (default 32).
 */
export function poleOfInaccessibility(
  vertices: Array<[number, number]>,
  fallback: { x: number; y: number },
  N = 32,
): { x: number; y: number } {
  if (vertices.length < 3) return fallback
  // Bounding box
  let minX = vertices[0]![0], maxX = minX
  let minY = vertices[0]![1], maxY = minY
  for (const [vx, vy] of vertices) {
    if (vx < minX) minX = vx
    if (vx > maxX) maxX = vx
    if (vy < minY) minY = vy
    if (vy > maxY) maxY = vy
  }
  const bboxW = maxX - minX
  const bboxH = maxY - minY
  if (bboxW < 1 || bboxH < 1) return fallback

  const stepX = bboxW / N
  const stepY = bboxH / N

  let bestDist = -1
  let best: { x: number; y: number } = fallback

  const n = vertices.length
  for (let ix = 0; ix < N; ix++) {
    for (let iy = 0; iy < N; iy++) {
      const cx = minX + (ix + 0.5) * stepX
      const cy = minY + (iy + 0.5) * stepY
      if (!pointInPolygon([cx, cy], vertices)) continue
      // Minimum distance (squared) from this candidate to the nearest
      // edge segment of the polygon. Using point-to-segment rather than
      // point-to-vertex gives correct clearance regardless of vertex
      // density along each edge.
      let minEdgeDistSq = Infinity
      for (let k = 0; k < n; k++) {
        const [ax, ay] = vertices[k]!
        const [bx, by] = vertices[(k + 1) % n]!
        const dSq = pointToSegmentDistSq(cx, cy, ax, ay, bx, by)
        if (dSq < minEdgeDistSq) {
          minEdgeDistSq = dSq
          // EXACT early exit: the winner is chosen by `minEdgeDistSq > bestDist`
          // below, and this value only ever DECREASES as we scan more edges. So
          // once it has fallen to bestDist or lower this candidate can no longer
          // win, and the remaining edges cannot change the outcome — only how
          // much work we do. Same `best`, same ties, far fewer iterations:
          // most grid candidates sit near an edge and bail within a few steps.
          if (minEdgeDistSq <= bestDist) break
        }
      }
      if (minEdgeDistSq > bestDist) {
        bestDist = minEdgeDistSq
        best = { x: cx, y: cy }
      }
    }
  }
  return best
}

// ─── Row / column alignment pass ─────────────────────────────────────────────

/**
 * Union-find (path-compressed, union-by-rank) over integer indices.
 * Used by computeAlignedBadgePositions to cluster territories into
 * horizontal rows and vertical columns.
 */
class UnionFind {
  private parent: number[]
  private rank: number[]
  constructor(size: number) {
    this.parent = Array.from({ length: size }, (_, i) => i)
    this.rank = new Array(size).fill(0)
  }
  find(x: number): number {
    if (this.parent[x] !== x) this.parent[x] = this.find(this.parent[x]!)
    return this.parent[x]!
  }
  union(x: number, y: number): void {
    const rx = this.find(x)
    const ry = this.find(y)
    if (rx === ry) return
    if (this.rank[rx]! < this.rank[ry]!) {
      this.parent[rx] = ry
    } else if (this.rank[rx]! > this.rank[ry]!) {
      this.parent[ry] = rx
    } else {
      this.parent[ry] = rx
      this.rank[rx] = (this.rank[rx] ?? 0) + 1
    }
  }
}

/**
 * Minimal territory shape needed by computeAlignedBadgePositions.
 * Only the fields actually consumed — avoids a full Territory import
 * (the function lives in a helper module shared with tests).
 */
export interface TerritoryForAlignment {
  id: string
  shape?: string
  adjacencies: string[]
}

/**
 * Compute per-territory badge positions that are (a) at the true pole
 * of inaccessibility for every territory (equidistant from surrounding
 * edges) and (b) snapped so that territories in the same horizontal
 * row or vertical column share a badge coordinate.
 *
 * Algorithm:
 *  1. Compute PoI for every territory (point furthest from any edge).
 *  2. Compute the axis-aligned bounding box of each territory polygon.
 *  3. Walk each adjacency pair and classify it as "same row" (bbox yMin
 *     and yMax within ALIGN_THRESHOLD of each other) or "same column"
 *     (bbox xMin and xMax within threshold).
 *  4. Union-find clusters the row-pairs and column-pairs separately.
 *  5. For each cluster with ≥ 2 members, compute the median PoI
 *     coordinate (y for rows, x for columns) and snap every member.
 *  6. After snapping, run pointInPolygon on the new badge position.
 *     If the snap pushed the badge outside the polygon, revert that
 *     territory to its per-territory PoI.
 *
 * Returns a Record<id, {x, y}> covering every territory in the input.
 * Memoisation is the caller's responsibility (typically a React.useMemo
 * that re-runs only when `state.map.territories` changes).
 *
 * @param territories  Array of territories to process.
 * @param N            Grid resolution for PoI (default 32, matches poleOfInaccessibility default).
 */
export function computeAlignedBadgePositions(
  territories: TerritoryForAlignment[],
  N = 32,
): Record<string, { x: number; y: number }> {
  const ALIGN_THRESHOLD = 8

  // Step 1: parse vertices + compute PoI for every territory.
  const vertsById = new Map<string, Array<[number, number]>>()
  const poiById = new Map<string, { x: number; y: number }>()
  for (const t of territories) {
    const verts = parsePolygonVertices(t.shape)
    vertsById.set(t.id, verts)
    // Use (0,0) as the fallback — the polygon must have at least 3 vertices
    // for PoI to be meaningful anyway. A degenerate polygon gets (0,0) which
    // is then clamped back to the territory's centroid by the caller.
    const poi = poleOfInaccessibility(verts, { x: 0, y: 0 }, N)
    poiById.set(t.id, poi)
  }

  // Step 2: bounding boxes.
  interface BBox { minX: number; maxX: number; minY: number; maxY: number }
  const bboxById = new Map<string, BBox>()
  for (const t of territories) {
    const verts = vertsById.get(t.id) ?? []
    if (verts.length === 0) continue
    let minX = verts[0]![0], maxX = minX
    let minY = verts[0]![1], maxY = minY
    for (const [vx, vy] of verts) {
      if (vx < minX) minX = vx
      if (vx > maxX) maxX = vx
      if (vy < minY) minY = vy
      if (vy > maxY) maxY = vy
    }
    bboxById.set(t.id, { minX, maxX, minY, maxY })
  }

  // Step 3 + 4: union-find for row groups (same yMin/yMax) and column
  // groups (same xMin/xMax). Each territory maps to a stable integer index.
  const idToIdx = new Map<string, number>()
  territories.forEach((t, i) => idToIdx.set(t.id, i))

  const rowUF = new UnionFind(territories.length)
  const colUF = new UnionFind(territories.length)

  const seen = new Set<string>()
  for (const t of territories) {
    const bA = bboxById.get(t.id)
    if (!bA) continue
    const iA = idToIdx.get(t.id)!
    for (const adjId of t.adjacencies) {
      const key = t.id < adjId ? `${t.id}|${adjId}` : `${adjId}|${t.id}`
      if (seen.has(key)) continue
      seen.add(key)
      const bB = bboxById.get(adjId)
      if (!bB) continue
      const iB = idToIdx.get(adjId)
      if (iB === undefined) continue

      // Same horizontal row: both top and bottom edges align.
      if (
        Math.abs(bA.minY - bB.minY) <= ALIGN_THRESHOLD &&
        Math.abs(bA.maxY - bB.maxY) <= ALIGN_THRESHOLD
      ) {
        rowUF.union(iA, iB)
      }

      // Same vertical column: both left and right edges align.
      if (
        Math.abs(bA.minX - bB.minX) <= ALIGN_THRESHOLD &&
        Math.abs(bA.maxX - bB.maxX) <= ALIGN_THRESHOLD
      ) {
        colUF.union(iA, iB)
      }
    }
  }

  // Step 5: compute median per cluster, snap badge coordinates.
  // We accumulate PoI coordinates per root, then compute the median.
  function median(vals: number[]): number {
    const sorted = vals.slice().sort((a, b) => a - b)
    const mid = Math.floor(sorted.length / 2)
    return sorted.length % 2 === 1
      ? sorted[mid]!
      : (sorted[mid - 1]! + sorted[mid]!) / 2
  }

  // Row groups → median y.
  const rowYByRoot = new Map<number, number[]>()
  const rowMembers = new Map<number, number[]>()
  for (const t of territories) {
    const idx = idToIdx.get(t.id)!
    const root = rowUF.find(idx)
    const poi = poiById.get(t.id)!
    if (!rowYByRoot.has(root)) { rowYByRoot.set(root, []); rowMembers.set(root, []) }
    rowYByRoot.get(root)!.push(poi.y)
    rowMembers.get(root)!.push(idx)
  }
  const rowMedianY = new Map<number, number>()
  for (const [root, ys] of rowYByRoot) {
    if ((rowMembers.get(root)?.length ?? 0) >= 2) {
      rowMedianY.set(root, median(ys))
    }
  }

  // Column groups → median x.
  const colXByRoot = new Map<number, number[]>()
  const colMembers = new Map<number, number[]>()
  for (const t of territories) {
    const idx = idToIdx.get(t.id)!
    const root = colUF.find(idx)
    const poi = poiById.get(t.id)!
    if (!colXByRoot.has(root)) { colXByRoot.set(root, []); colMembers.set(root, []) }
    colXByRoot.get(root)!.push(poi.x)
    colMembers.get(root)!.push(idx)
  }
  const colMedianX = new Map<number, number>()
  for (const [root, xs] of colXByRoot) {
    if ((colMembers.get(root)?.length ?? 0) >= 2) {
      colMedianX.set(root, median(xs))
    }
  }

  // Step 6: build final positions, clamping snapped coordinates back
  // to the PoI when the snap would push the badge outside the polygon.
  const result: Record<string, { x: number; y: number }> = {}
  for (const t of territories) {
    const idx = idToIdx.get(t.id)!
    const poi = poiById.get(t.id)!
    const verts = vertsById.get(t.id) ?? []

    let bx = poi.x
    let by = poi.y

    const rRoot = rowUF.find(idx)
    const medY = rowMedianY.get(rRoot)
    if (medY !== undefined) by = medY

    const cRoot = colUF.find(idx)
    const medX = colMedianX.get(cRoot)
    if (medX !== undefined) bx = medX

    // Clamp: if the snapped position falls outside the polygon, revert
    // just the snapped axis (or both) to the per-territory PoI.
    if (verts.length >= 3 && !pointInPolygon([bx, by], verts)) {
      bx = poi.x
      by = poi.y
    }

    result[t.id] = { x: bx, y: by }
  }
  return result
}

/**
 * Overlay manual badge anchors on top of a computed badge-position map.
 *
 * For every territory that carries a `badgeAnchor` field (set via the map
 * editor's drag-to-place), its entry in `base` is replaced with the manual
 * coordinate verbatim — no PoI correction, no alignment-snap.  Territories
 * without `badgeAnchor` are left as-is (computed PoI + alignment).
 *
 * This is a pure function so it can be unit-tested without React/RN imports.
 *
 * @param base       Record produced by `computeAlignedBadgePositions`.
 * @param territories  The same territory array that produced `base`.
 */
export function applyBadgeAnchors(
  base: Record<string, { x: number; y: number }>,
  territories: ReadonlyArray<{ id: string; badgeAnchor?: { x: number; y: number } }>,
): Record<string, { x: number; y: number }> {
  const out = { ...base }
  for (const t of territories) {
    if (t.badgeAnchor) out[t.id] = t.badgeAnchor
  }
  return out
}

/**
 * Pick an interior anchor for a region bonus badge.
 *
 * Strategy (in priority order):
 *  1. If the region supplies a `shape` (union SVG path), parse it and
 *     compute the pole of inaccessibility — the point furthest from any
 *     edge, guaranteed interior, always a safe badge location.
 *  2. Otherwise average the `position` centroids of every territory in
 *     the region. This is a cheap O(n) fallback that is good-enough for
 *     convex regions but may land outside concave ones.
 *
 * The fallback point is only used when `poleOfInaccessibility` cannot
 * find an interior sample (degenerate polygon with < 3 vertices), which
 * should not occur on any real map.
 *
 * @param regionShape   SVG path `d` string for the region union outline,
 *                      or undefined / empty when not available.
 * @param territoryPositions  Centroid positions of every territory that
 *                      belongs to this region. Used as the fallback.
 * @returns  { x, y } in viewBox coordinates.
 */
export function regionBadgeAnchor(
  regionShape: string | undefined,
  territoryPositions: Array<{ x: number; y: number }>,
): { x: number; y: number } {
  // Compute the average centroid as fallback in any case.
  const fallback: { x: number; y: number } = territoryPositions.length > 0
    ? {
        x: territoryPositions.reduce((s, p) => s + p.x, 0) / territoryPositions.length,
        y: territoryPositions.reduce((s, p) => s + p.y, 0) / territoryPositions.length,
      }
    : { x: 0, y: 0 }

  if (!regionShape) return fallback

  const verts = parsePolygonVertices(regionShape)
  if (verts.length < 3) return fallback

  return poleOfInaccessibility(verts, fallback)
}

/**
 * Decide whether two polygons (given as vertex lists) share an edge —
 * the topological signal for "land border" vs "sea route". Two vertices
 * within `epsilon` units are treated as the same point; the polygons
 * share an edge when at least 2 vertex pairs coincide (a single shared
 * vertex would be a corner, not an edge).
 */
export function polygonsShareEdge(
  a: Array<[number, number]>,
  b: Array<[number, number]>,
  epsilon = 1.5,
): boolean {
  const eps2 = epsilon * epsilon
  let hits = 0
  for (const va of a) {
    for (const vb of b) {
      const dx = va[0] - vb[0]
      const dy = va[1] - vb[1]
      if (dx * dx + dy * dy <= eps2) {
        hits++
        if (hits >= 2) return true
        break
      }
    }
  }
  return false
}

/** Mean of a polygon's vertices. Good enough to give a direction. */
function vertexMean(p: Array<[number, number]>): [number, number] {
  let sx = 0
  let sy = 0
  for (const [x, y] of p) {
    sx += x
    sy += y
  }
  return [sx / p.length, sy / p.length]
}

/**
 * The vertices of `a` that sit within `epsilon` of some vertex of `b` — i.e. the
 * stretch where the two polygons touch. Same epsilon as `polygonsShareEdge` so
 * the two agree about what "touching" means.
 */
function contactVertices(
  a: Array<[number, number]>,
  b: Array<[number, number]>,
  epsilon: number,
): Array<[number, number]> {
  const eps2 = epsilon * epsilon
  const out: Array<[number, number]> = []
  for (const va of a) {
    for (const vb of b) {
      const dx = va[0] - vb[0]
      const dy = va[1] - vb[1]
      if (dx * dx + dy * dy <= eps2) {
        out.push(va)
        break
      }
    }
  }
  return out
}

/** The two points furthest apart in a set — for a contact stretch, its ends. */
function furthestPair(
  pts: Array<[number, number]>,
): [[number, number], [number, number]] | null {
  if (pts.length < 2) return null
  let best: [[number, number], [number, number]] | null = null
  let bestD = -1
  for (let i = 0; i < pts.length; i++) {
    for (let j = i + 1; j < pts.length; j++) {
      const dx = pts[i]![0] - pts[j]![0]
      const dy = pts[i]![1] - pts[j]![1]
      const d = dx * dx + dy * dy
      if (d > bestD) {
        bestD = d
        best = [pts[i]!, pts[j]!]
      }
    }
  }
  return best
}

/**
 * A visible dashed "gate" straddling the border between two touching polygons,
 * for `forceSeaRoutes`.
 *
 * Returns null when the polygons do NOT touch — those pairs are already sea
 * routes and should use `closestVertexPair` like any other.
 *
 * WHY NOT JUST USE THE SHARED BORDER. The obvious approach — anchor the dash to
 * the shared border span — produces a dash 2.9 units long for the St. Marys
 * strait, because Superior and Huron touch at what is essentially a point (the
 * traced channel is a hair). Drawn, but invisible, and invisible in a screenshot
 * review too.
 *
 * So instead the gate is centred on the contact and pointed from one lake to
 * the other (`vertexMean` to `vertexMean`), with a floor on its length. That
 * reads the same way every other sea route does — a dash leaving one territory
 * and arriving in the next — and it cannot degenerate however small the contact
 * is.
 */
/**
 * Cosmetic per-route adjustment. Applies to ANY sea route, not just forced
 * gates: "rotate / resize / move this dash" is the same operation whether the
 * dash spans a real gap or straddles a touching border.
 */
export interface SeaRouteTuning {
  /**
   * 'edge-midpoints': the closest pair of polygon edge midpoints instead of
   * vertices. 'centers': the line between the two polygons' centres, clipped
   * to their outlines (centersCrossingSegment).
   */
  endpointMode?: 'edge-midpoints' | 'centers'
  /** Absolute angle in degrees; 0 = right, 90 = down (SVG y grows downward). */
  angleDeg?: number
  /** Nudge the midpoint, in viewBox units. */
  offset?: { x?: number; y?: number }
  /** Length in viewBox units. */
  length?: number
}

/**
 * Find the closest pair of polygon edge midpoints.
 *
 * This is useful for deliberate crossings between regular hexes: a route
 * should leave through the middle of the facing long sides, not through one
 * of the two equally-close vertices at either end of those sides.
 */
export function closestEdgeMidpointPair(
  a: Array<[number, number]>,
  b: Array<[number, number]>,
): { from: [number, number]; to: [number, number] } | null {
  if (a.length < 2 || b.length < 2) return null
  const midpoints = (vertices: Array<[number, number]>) => vertices.map((point, index) => {
    const next = vertices[(index + 1) % vertices.length]!
    return [(point[0] + next[0]) / 2, (point[1] + next[1]) / 2] as [number, number]
  })
  const am = midpoints(a)
  const bm = midpoints(b)
  let bestDistSq = Infinity
  let bestA = am[0]!
  let bestB = bm[0]!
  for (const va of am) {
    for (const vb of bm) {
      const dx = va[0] - vb[0]
      const dy = va[1] - vb[1]
      const d = dx * dx + dy * dy
      if (d < bestDistSq) {
        bestDistSq = d
        bestA = va
        bestB = vb
      }
    }
  }
  return { from: bestA, to: bestB }
}

/**
 * The line between the two polygons' centres, clipped to their outlines: it
 * leaves `a` where the centre-to-centre line last exits `a` and enters `b`
 * where that line first enters `b`. A landing or strait then reads as "this
 * territory to that one" through the middle of each facing coast, rather than
 * corner to corner (closestVertexPair) — which on jagged coasts picks whichever
 * two bumps happen to be nearest.
 *
 * The centre is the area centroid, so a concave bite in the coast does not
 * pull it around the way a vertex mean would. Returns null when the line does
 * not cross both outlines (degenerate polygons, or one inside the other).
 */
export function centersCrossingSegment(
  a: Array<[number, number]>,
  b: Array<[number, number]>,
): { from: [number, number]; to: [number, number] } | null {
  if (a.length < 3 || b.length < 3) return null
  const ca = polygonCentroid(a)
  const cb = polygonCentroid(b)
  const exitA = outlineCrossing(ca, cb, a, 'last')
  const entryB = outlineCrossing(ca, cb, b, 'first')
  if (!exitA || !entryB) return null
  return { from: exitA, to: entryB }
}

/** Area centroid of a simple polygon (falls back to the vertex mean when degenerate). */
function polygonCentroid(p: Array<[number, number]>): [number, number] {
  let area = 0, cx = 0, cy = 0
  for (let i = 0; i < p.length; i++) {
    const [x1, y1] = p[i]!
    const [x2, y2] = p[(i + 1) % p.length]!
    const cross = x1 * y2 - x2 * y1
    area += cross
    cx += (x1 + x2) * cross
    cy += (y1 + y2) * cross
  }
  if (Math.abs(area) < 1e-9) return vertexMean(p)
  return [cx / (3 * area), cy / (3 * area)]
}

/**
 * Where the segment from→to crosses the polygon outline: the crossing nearest
 * `from` ('first') or farthest from it ('last'), as a point. Null if it never
 * crosses.
 */
function outlineCrossing(
  from: [number, number],
  to: [number, number],
  vertices: Array<[number, number]>,
  which: 'first' | 'last',
): [number, number] | null {
  const dx = to[0] - from[0], dy = to[1] - from[1]
  let best: number | null = null
  for (let i = 0; i < vertices.length; i++) {
    const [x1, y1] = vertices[i]!
    const [x2, y2] = vertices[(i + 1) % vertices.length]!
    const ex = x2 - x1, ey = y2 - y1
    const denom = dx * ey - dy * ex
    if (Math.abs(denom) < 1e-12) continue // parallel
    // from + t·d  ==  v1 + u·e
    const t = ((x1 - from[0]) * ey - (y1 - from[1]) * ex) / denom
    const u = ((x1 - from[0]) * dy - (y1 - from[1]) * dx) / denom
    if (t < 0 || t > 1 || u < 0 || u > 1) continue
    if (best === null || (which === 'first' ? t < best : t > best)) best = t
  }
  if (best === null) return null
  return [from[0] + dx * best, from[1] + dy * best]
}

/**
 * Re-aim / re-size / re-centre a route segment.
 *
 * Kept separate from how the segment was DERIVED (closest-vertex pair for a real
 * gap, a perpendicular gate for a touching border) so both get the same knobs
 * and the map editor can preview a drag without recomputing any geometry.
 *
 * A no-op when `tuning` is empty, so it is safe to call unconditionally.
 */
export function applySeaRouteTuning(
  seg: { from: [number, number]; to: [number, number] },
  tuning: SeaRouteTuning | undefined,
): { from: [number, number]; to: [number, number] } {
  if (!tuning || (tuning.angleDeg === undefined && !tuning.offset && tuning.length === undefined)) {
    return seg
  }
  const mx = (seg.from[0] + seg.to[0]) / 2
  const my = (seg.from[1] + seg.to[1]) / 2
  const baseLen = Math.hypot(seg.to[0] - seg.from[0], seg.to[1] - seg.from[1])
  const baseAng = Math.atan2(seg.to[1] - seg.from[1], seg.to[0] - seg.from[0])
  const len = tuning.length ?? baseLen
  const ang = tuning.angleDeg !== undefined ? (tuning.angleDeg * Math.PI) / 180 : baseAng
  const cx = mx + (tuning.offset?.x ?? 0)
  const cy = my + (tuning.offset?.y ?? 0)
  const half = len / 2
  return {
    from: [cx - Math.cos(ang) * half, cy - Math.sin(ang) * half],
    to: [cx + Math.cos(ang) * half, cy + Math.sin(ang) * half],
  }
}

export function forcedCrossingSegment(
  a: Array<[number, number]>,
  b: Array<[number, number]>,
  minLength = 28,
  epsilon = 1.5,
): { from: [number, number]; to: [number, number] } | null {
  if (a.length === 0 || b.length === 0) return null
  const contacts = contactVertices(a, b, epsilon)
  if (contacts.length === 0) return null

  const mid: [number, number] = [
    contacts.reduce((t, p) => t + p[0], 0) / contacts.length,
    contacts.reduce((t, p) => t + p[1], 0) / contacts.length,
  ]

  const ca = vertexMean(a)
  const cb = vertexMean(b)

  // Direction: PERPENDICULAR TO THE SHARED BORDER. A crossing should cut the
  // border at right angles, the way a strait does — using the centroid-to-
  // centroid direction instead drew the Niagara gate on a diagonal, because
  // Erie's cell sits down-left of Ontario's even though they meet along an
  // almost-horizontal edge.
  let dx = 0
  let dy = 0
  const span = furthestPair(contacts)
  if (span) {
    const bx = span[1][0] - span[0][0]
    const by = span[1][1] - span[0][1]
    const bl = Math.hypot(bx, by)
    if (bl > epsilon) {
      dx = -by / bl
      dy = bx / bl
      // Perpendicular has two directions; pick the one pointing from a to b.
      if ((cb[0] - ca[0]) * dx + (cb[1] - ca[1]) * dy < 0) {
        dx = -dx
        dy = -dy
      }
    }
  }

  if (dx === 0 && dy === 0) {
    // Point contact (or a degenerate span) — no border direction to be
    // perpendicular to, so fall back to pointing one lake at the other.
    dx = cb[0] - ca[0]
    dy = cb[1] - ca[1]
    const len = Math.hypot(dx, dy)
    if (len === 0) {
      dx = 1
      dy = 0
    } else {
      dx /= len
      dy /= len
    }
  }

  const half = minLength / 2
  return {
    from: [mid[0] - dx * half, mid[1] - dy * half],
    to: [mid[0] + dx * half, mid[1] + dy * half],
  }
}

// ─── Per-edge classification ──────────────────────────────────────────────────

/**
 * Minimal territory description required by `classifyTerritoryEdges`.
 */
export interface TerritoryEdgeInput {
  id: string
  regionId?: string
  adjacencies: string[]
  shape?: string
}

/**
 * Result of edge classification for the map's polygon-overlay layers.
 * Both values are `Record<territoryId, svgPathD>` where the path is a
 * multi-subpath string of `M x0 y0 L x1 y1` segments — one per edge —
 * ready to pass directly to an SVG `<Path d={...}>` element.
 */
export interface EdgeClassificationResult {
  /**
   * Edges that touch the ocean — not shared by any adjacent territory's
   * polygon. Rendered by the coastal overlay at full stroke width.
   */
  coastalEdges: Record<string, string>
  /**
   * Edges shared with a territory in a DIFFERENT region. Rendered by the
   * cross-region overlay at full stroke width with a clipPath so each
   * side shows its own region colour. Same-region shared edges are absent
   * from both maps — the thin base polygon stroke handles them.
   */
  crossRegionEdges: Record<string, string>
}

/**
 * Classify every polygon edge in `territories` into three buckets:
 *   1. Same-region shared edge → omitted from result (polygon stroke handles it).
 *   2. Cross-region shared edge → included in `crossRegionEdges`.
 *   3. Unshared / coastal edge → included in `coastalEdges`.
 *
 * Edge-matching uses a midpoint + direction algorithm (see below) so that
 * adjacent polygons that subdivide a shared border with different vertex
 * densities (e.g. one polygon has 3 vertices along a stretch, the other 5)
 * are still recognised as sharing that border.
 *
 * Each polygon is expected to be a convex or simple polygon described by
 * consecutive on-curve vertices from `parsePolygonVertices`. Edges wrapping
 * from the last vertex back to the first are included.
 *
 * Pure function — no React, no DOM. Memoised by the caller (MapView useMemo).
 * Exported so unit tests can exercise it without mounting MapView.
 *
 * @param territories  Territory list from `state.map.territories`.
 */
export function classifyTerritoryEdges(
  territories: ReadonlyArray<TerritoryEdgeInput>,
  opts: { midEpsilon?: number; angleEpsilonDeg?: number } = {},
): EdgeClassificationResult {
  // ── Midpoint + direction matching ────────────────────────────────────────
  //
  // The previous algorithm required BOTH endpoints of an edge (p0, p1) on
  // polygon A to fall within EPSILON of the corresponding endpoint pair on
  // polygon B.  This fails on the Risk world map's Yukon/Canada region where
  // adjacent polygons are authored with different vertex densities along the
  // shared border: one side may have 3 vertices across a stretch that the
  // other side represents with 5.  The old vertex-pair check misses those
  // matches and misclassifies valid internal borders as coastal (thick stroke
  // on a same-region line).
  //
  // Replacement algorithm:
  //   For each edge (p0, p1) of polygon A:
  //     midA  = midpoint((p0, p1))
  //     dirA  = atan2(p1.y - p0.y, p1.x - p0.x) normalised to [0, π)
  //   For each candidate edge (q0, q1) of polygon B:
  //     midB  = midpoint((q0, q1))
  //     dirB  = atan2(q1.y - q0.y, q1.x - q0.x) normalised to [0, π)
  //   Match when:
  //     dist(midA, midB) ≤ MID_EPSILON  AND  |angleDiff(dirA, dirB)| ≤ DIR_EPSILON
  //
  // MID_EPSILON = 8 px — generous enough to absorb the slop on the Risk world
  // map (polygons independently traced from raster source, shared vertices up
  // to ~7 px apart) while still comfortably below the ~30 px gap between
  // unrelated parallel edges on the densest map (Carpathia).
  //
  // DIR_EPSILON = 10° (π/18 rad) — tight enough to reject X-crossing
  // artifacts where an unrelated nearby edge runs perpendicular to ours.
  //
  // Angle normalisation to [0, π): a border from A→B and the matching edge
  // from B→A point in OPPOSITE directions, so without normalisation the angle
  // difference would always be ~180° for genuinely shared edges.
  const MID_EPSILON = opts.midEpsilon ?? 8
  const MID_EPS_SQ = MID_EPSILON * MID_EPSILON
  const DIR_EPSILON = ((opts.angleEpsilonDeg ?? 10) * Math.PI) / 180

  /** Normalise an angle (radians) to [0, π). */
  function normAngle(a: number): number {
    // Bring into [0, 2π)
    let r = a % (2 * Math.PI)
    if (r < 0) r += 2 * Math.PI
    // Fold the upper half back onto [0, π) so opposing directions match.
    if (r >= Math.PI) r -= Math.PI
    return r
  }

  // Build vertex lists once.
  const verticesById = new Map<string, Array<[number, number]>>()
  for (const t of territories) {
    verticesById.set(t.id, parsePolygonVertices(t.shape))
  }

  // Index territory → region.
  const regionById = new Map<string, string | undefined>()
  for (const t of territories) regionById.set(t.id, t.regionId)

  /**
   * Return the id of the first territory in `candidateIds` whose polygon
   * shares the edge (p0, p1) — determined by midpoint proximity and
   * direction alignment — or null if none does.
   */
  function sharedWithNeighbour(
    p0: [number, number],
    p1: [number, number],
    candidateIds: string[],
  ): string | null {
    const midAx = (p0[0] + p1[0]) / 2
    const midAy = (p0[1] + p1[1]) / 2
    const dirA = normAngle(Math.atan2(p1[1] - p0[1], p1[0] - p0[0]))

    for (const nId of candidateIds) {
      const nv = verticesById.get(nId)
      if (!nv || nv.length < 2) continue
      const n = nv.length
      for (let k = 0; k < n; k++) {
        const q0 = nv[k]!
        const q1 = nv[(k + 1) % n]!

        const midBx = (q0[0] + q1[0]) / 2
        const midBy = (q0[1] + q1[1]) / 2
        const dmx = midAx - midBx
        const dmy = midAy - midBy
        if (dmx * dmx + dmy * dmy > MID_EPS_SQ) continue

        const dirB = normAngle(Math.atan2(q1[1] - q0[1], q1[0] - q0[0]))
        const angleDiff = Math.abs(dirA - dirB)
        // Angle difference is already in [0, π) for both — the max wrap-around
        // distance within [0, π) is π/2, but we check the raw diff first then
        // the wrapped complement to handle the 0/π boundary.
        const wrappedDiff = Math.min(angleDiff, Math.PI - angleDiff)
        if (wrappedDiff <= DIR_EPSILON) {
          return nId
        }
      }
    }
    return null
  }

  const coastalEdges: Record<string, string> = {}
  const crossRegionEdges: Record<string, string> = {}

  for (const t of territories) {
    if (!t.shape) continue
    const verts = verticesById.get(t.id)
    if (!verts || verts.length < 2) continue
    const n = verts.length
    const coastalSegs: string[] = []
    const crossSegs: string[] = []

    for (let k = 0; k < n; k++) {
      const p0 = verts[k]!
      const p1 = verts[(k + 1) % n]!
      const sharingId = sharedWithNeighbour(p0, p1, t.adjacencies)
      if (sharingId === null) {
        coastalSegs.push(`M ${p0[0]} ${p0[1]} L ${p1[0]} ${p1[1]}`)
      } else if (regionById.get(sharingId) !== t.regionId) {
        crossSegs.push(`M ${p0[0]} ${p0[1]} L ${p1[0]} ${p1[1]}`)
      }
      // Same-region shared edge: handled by the thin polygon stroke.
    }

    if (coastalSegs.length > 0) coastalEdges[t.id] = coastalSegs.join(' ')
    if (crossSegs.length > 0) crossRegionEdges[t.id] = crossSegs.join(' ')
  }

  return { coastalEdges, crossRegionEdges }
}

/**
 * Troop-count font on a map with MapDef.fitTroopBadges: two fixed sizes, so the
 * board reads uniform instead of every space getting its own.
 *
 * - SMALL (`TROOP_BADGE_SMALL_SCALE` of the usual size) everywhere a
 *   three-digit count fits round the territory's badge anchor.
 * - XSMALL (`TROOP_BADGE_XSMALL_SCALE`) where it doesn't: `badgeSize: 'xsmall'`,
 *   baked by the map's build (Normulli Sector's towers, base crystals and the
 *   wedges the towers cut short).
 *
 * The tier is decided from the widest label, so a territory's count never
 * changes size as it grows, except on compact symbols that opt in with
 * `badgeShrinksWithDigits` (Normulli's towers and crystals), whose count steps
 * down at two digits and again at three. Normulli's trace checks the fit with the same
 * SMALL scale (scripts/maps/trace-normulli-sector.py, BADGE_SMALL_SCALE).
 */
export const TROOP_BADGE_SMALL_SCALE = 0.66
export const TROOP_BADGE_XSMALL_SCALE = 0.52
/** Territory.badgeShrinksWithDigits: the step down at two digits and at three or more. */
export const TROOP_BADGE_TWO_DIGIT_SCALE = 0.88
export const TROOP_BADGE_THREE_DIGIT_SCALE = 0.76
export function tieredBadgeFontSize(
  defaultSize: number,
  badgeSize: 'xsmall' | undefined,
  label = '',
  shrinksWithDigits = false,
): number {
  const tier = defaultSize * (badgeSize === 'xsmall' ? TROOP_BADGE_XSMALL_SCALE : TROOP_BADGE_SMALL_SCALE)
  if (!shrinksWithDigits || label.length < 2) return tier
  return tier * (label.length >= 3 ? TROOP_BADGE_THREE_DIGIT_SCALE : TROOP_BADGE_TWO_DIGIT_SCALE)
}
